// src/features/ucits-etf/UcitsEtf.tsx
//
// The UCITS Alpha Engine dashboard — the capstone page that ties the
// feature's three islands together behind the standard authenticated shell:
//
//   <Layout sidebar={<Sidebar />}>   ← whole-project navigation
//     <GlobalSearchBar />            ← platform-wide command palette
//     <AutoSuggest />                ← UCITS-scoped similarity search
//     <BreakoutBoard />              ← nightly quantitative signals
//     <Screener />                   ← in-memory Polars screener
//     <SimilarityModal />            ← Numba cosine peers (Recharts)
//
// Selecting a fund from the AutoSuggest opens a modal that visualises the
// sector-overlap cosine-similarity peers returned by the /{symbol}/similar
// endpoint. Everything is dark-mode aware and responsive down to mobile.

import { useState } from 'react';

import { Activity, ArrowDown, Loader2, Network, Sparkles, X } from 'lucide-react';
import {
    Bar,
    BarChart,
    Cell,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { GlobalSearchBar } from '../global-search';
import { Layout, Sidebar } from '../../shared/ui';
import { useSimilarEtfs } from './api/etfQueries';
import { AutoSuggest } from './components/AutoSuggest';
import { BreakoutBoard } from './components/BreakoutBoard';
import { CapitalFlowsDashboard } from './components/CapitalFlowsDashboard';
import { Screener } from './components/Screener';
import { etfInfoPath } from './routes';

/**
 * Open a fund's ETF Info deep-dive in a safe new tab. The `noopener,noreferrer`
 * window feature prevents the opened page from hijacking `window.opener`
 * (tab-nabbing) and strips the referrer.
 */
const openEtfInfo = (symbol: string): void => {
    window.open(etfInfoPath(symbol), '_blank', 'noopener,noreferrer');
};

/**
 * Clickable Recharts Y-axis category tick. Renders the fund symbol as an
 * underlined, pointer-cursored label that opens the ETF Info page on click.
 * Recharts injects `x`, `y` and `payload` (payload.value === the symbol).
 */
const ClickableYAxisTick = (props: {
    x?: number;
    y?: number;
    payload?: { value: string };
}) => {
    const { x, y, payload } = props;
    const symbol = payload?.value ?? '';
    return (
        <text
            x={x}
            y={y}
            dy={4}
            textAnchor="end"
            style={{
                fill: '#64748b',
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
                textDecoration: 'underline',
            }}
            onClick={(e) => {
                e.stopPropagation();
                if (symbol) openEtfInfo(symbol);
            }}
        >
            {symbol}
        </text>
    );
};

export default function UcitsEtfDashboard() {
    const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null);
    const {
        data: similarEtfs,
        isLoading: isSimilarLoading,
    } = useSimilarEtfs(selectedSymbol);

    const rankedPeers = similarEtfs ?? [];

    const scrollToScreener = () => {
        document
            .getElementById('advanced-ucits-etf-screener')
            ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    // Recharts data — top 5 overlap scores as percentages.
    const chartData = rankedPeers.slice(0, 5).map((etf) => ({
        name: etf.symbol,
        score: Number(((etf.similarity_score ?? 0) * 100).toFixed(1)),
    }));

    return (
        <Layout
            sidebar={<Sidebar />}
            title="UCITS ETF Alpha Engine"
            subtitle="Advanced quantitative breakout models, screening and sector-overlap similarity for the European UCITS ETF universe."
        >
            {/* Platform-wide asset command palette. */}
            <div className="mb-8">
                <GlobalSearchBar />
            </div>

            {/* UCITS-scoped similarity search — selecting a fund opens the
                Numba cosine-similarity modal. */}
            <section className="mb-14">
                <div className="surface-card flex flex-col gap-5 md:flex-row md:items-center md:justify-between
                                bg-gradient-to-br from-white to-emerald-50/40
                                dark:from-slate-800/60 dark:to-emerald-900/10">
                    <div className="flex items-center gap-4">
                        <div className="p-2.5 rounded-xl bg-emerald-600 text-white shadow-lg shadow-emerald-500/20">
                            <Activity size={24} />
                        </div>
                        <div>
                            <h2 className="text-xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100">
                                Find Similar Funds
                            </h2>
                            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                                Vector cosine similarity over sector allocations,
                                computed instantly using advanced proprietary algorithms.
                            </p>
                        </div>
                    </div>
                    <div className="w-full md:max-w-md">
                        <AutoSuggest onSelect={(symbol) => setSelectedSymbol(symbol)} />
                    </div>
                </div>
                <div className="mt-5 flex justify-end">
                    <button
                        type="button"
                        onClick={scrollToScreener}
                        className="group relative inline-flex items-center gap-2.5 overflow-hidden rounded-full px-6 py-3
                                   text-sm font-bold tracking-tight text-white
                                   bg-gradient-to-r from-emerald-500 via-teal-500 to-indigo-600
                                   shadow-lg shadow-emerald-500/30 ring-1 ring-white/20
                                   transition-all duration-300 ease-out
                                   hover:-translate-y-0.5 hover:shadow-xl hover:shadow-indigo-500/40
                                   active:translate-y-0
                                   focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-emerald-500
                                   dark:focus-visible:ring-offset-slate-900"
                    >
                        <span
                            aria-hidden="true"
                            className="pointer-events-none absolute inset-y-0 -left-full w-1/2 skew-x-[-20deg]
                                       bg-gradient-to-r from-transparent via-white/40 to-transparent
                                       transition-all duration-700 ease-out group-hover:left-[150%]"
                        />
                        <Sparkles size={16} className="relative transition-transform duration-300 group-hover:rotate-12" />
                        <span className="relative">Explore Advanced UCITS ETF Screener</span>
                        <span className="relative flex h-6 w-6 items-center justify-center rounded-full bg-white/20">
                            <ArrowDown size={14} className="animate-bounce" />
                        </span>
                    </button>
                </div>
            </section>

            {/* Daily quantitative breakout setups. */}
            <section className="mb-14">
                <div className="mb-6">
                    <h2 className="section-title">Daily Technical Setups</h2>
                    <p className="muted text-sm mt-1">
                        Algorithmic BUY / ACCUMULATE / REDUCE / SELL ideas, ranked
                        daily by conviction.
                    </p>
                </div>
                <BreakoutBoard />
            </section>

            {/* Smart-money capital flows. */}
            <section className="mb-14">
                <div className="mb-6">
                    <h2 className="section-title">Capital Flows</h2>
                    <p className="muted text-sm mt-1">
                        Inferred daily net flows — new capital entering (green)
                        vs. leaving (red) each fund, NAV performance stripped
                        out.
                    </p>
                </div>
                <CapitalFlowsDashboard />
            </section>

            {/* In-memory screener. */}
            <section id="advanced-ucits-etf-screener" className="scroll-mt-24">
                <div className="mb-6">
                    <h2 className="section-title">Advanced UCITS ETF Screener</h2>
                    <p className="muted text-sm mt-1">
                        Scan the European ETF universe to include in your investment portfolio.
                    </p>
                </div>
                <Screener />
            </section>

            {/* Similarity modal ------------------------------------------- */}
            {selectedSymbol && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in
                               bg-slate-900/50 backdrop-blur-sm"
                    onClick={() => setSelectedSymbol(null)}
                    role="dialog"
                    aria-modal="true"
                >
                    <div
                        className="w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden rounded-3xl shadow-2xl
                                   bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between gap-3 p-6
                                        border-b border-slate-100 dark:border-slate-700
                                        bg-slate-50 dark:bg-slate-800/60">
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-400">
                                    <Network size={24} />
                                </div>
                                <div className="min-w-0">
                                    <h3 className="font-extrabold text-xl text-slate-900 dark:text-slate-100 truncate">
                                        Similar to{' '}
                                        <a
                                            href={etfInfoPath(selectedSymbol)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            className="text-indigo-600 dark:text-indigo-400 underline decoration-dotted underline-offset-4 hover:decoration-solid
                                                       rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                                        >
                                            {selectedSymbol}
                                        </a>
                                    </h3>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                        Sector-overlap cosine similarity based on proprietary algorithmic methodology.
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openEtfInfo(selectedSymbol);
                                    }}
                                    aria-label={`Open ETF info for ${selectedSymbol} in a new tab`}
                                    className="px-3 py-1.5 rounded-lg text-xs font-semibold shadow-sm transition-colors
                                               bg-indigo-600 text-white hover:bg-indigo-700
                                               dark:bg-indigo-500 dark:hover:bg-indigo-600
                                               focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                                >
                                    More Info…
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setSelectedSymbol(null)}
                                    aria-label="Close"
                                    className="p-2 rounded-full transition-colors
                                               bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600
                                               text-slate-600 dark:text-slate-300
                                               focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                        </div>

                        <div className="overflow-y-auto p-8 flex flex-col md:flex-row gap-8">
                            {isSimilarLoading ? (
                                <div className="w-full py-24 flex flex-col items-center justify-center gap-4 text-slate-400 dark:text-slate-500">
                                    <Loader2
                                        className="animate-spin text-indigo-500"
                                        size={32}
                                    />
                                    <p className="text-sm font-medium">
                                        Executing vector math engine…
                                    </p>
                                </div>
                            ) : rankedPeers.length === 0 ? (
                                <div className="w-full py-24 text-center text-sm font-medium text-slate-400 dark:text-slate-500">
                                    No sector-overlap peers found for{' '}
                                    {selectedSymbol}.
                                </div>
                            ) : (
                                <>
                                    {/* Recharts visualisation. */}
                                    <div className="w-full md:w-1/2 min-h-[300px]">
                                        <h4 className="text-sm font-bold uppercase tracking-widest text-center mb-6 text-slate-600 dark:text-slate-300">
                                            Top 5 Overlap Scores
                                        </h4>
                                        <ResponsiveContainer width="100%" height={280}>
                                            <BarChart
                                                data={chartData}
                                                layout="vertical"
                                                margin={{ top: 0, right: 40, left: 0, bottom: 0 }}
                                            >
                                                <XAxis
                                                    type="number"
                                                    domain={[0, 100]}
                                                    hide
                                                />
                                                <YAxis
                                                    dataKey="name"
                                                    type="category"
                                                    axisLine={false}
                                                    tickLine={false}
                                                    tick={<ClickableYAxisTick />}
                                                    width={92}
                                                />
                                                <Tooltip
                                                    cursor={{ fill: 'rgba(148,163,184,0.12)' }}
                                                    formatter={(value: number) => [
                                                        `${value}%`,
                                                        'Overlap',
                                                    ]}
                                                    contentStyle={{
                                                        borderRadius: '12px',
                                                        border: 'none',
                                                        boxShadow:
                                                            '0 4px 12px -1px rgb(0 0 0 / 0.15)',
                                                    }}
                                                />
                                                <Bar
                                                    dataKey="score"
                                                    radius={[0, 4, 4, 0]}
                                                    barSize={24}
                                                    onClick={(_, index) => {
                                                        const symbol =
                                                            chartData[index]?.name;
                                                        if (symbol)
                                                            openEtfInfo(symbol);
                                                    }}
                                                >
                                                    {chartData.map((_, index) => (
                                                        <Cell
                                                            key={`cell-${index}`}
                                                            cursor="pointer"
                                                            fill={
                                                                index === 0
                                                                    ? '#4f46e5'
                                                                    : '#94a3b8'
                                                            }
                                                        />
                                                    ))}
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>

                                    {/* Ranked list. */}
                                    <div className="w-full md:w-1/2 space-y-3">
                                        {rankedPeers.slice(0, 6).map((etf, idx) => (
                                            <div
                                                key={etf.symbol}
                                                className="flex items-center justify-between gap-3 p-4 rounded-2xl transition-colors
                                                           border border-slate-100 dark:border-slate-700
                                                           hover:bg-slate-50 dark:hover:bg-slate-800/60"
                                            >
                                                <div className="flex items-center gap-4 min-w-0">
                                                    <div
                                                        className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center font-bold text-xs ${
                                                            idx === 0
                                                                ? 'bg-indigo-600 text-white shadow-md'
                                                                : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300'
                                                        }`}
                                                    >
                                                        {idx + 1}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <a
                                                            href={etfInfoPath(
                                                                etf.symbol,
                                                            )}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            onClick={(e) =>
                                                                e.stopPropagation()
                                                            }
                                                            className="font-bold text-slate-900 dark:text-slate-100 hover:underline decoration-dotted underline-offset-4
                                                                       rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                                                        >
                                                            {etf.symbol}
                                                        </a>
                                                        <div
                                                            className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-[160px]"
                                                            title={etf.name ?? undefined}
                                                        >
                                                            {etf.name}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="text-right shrink-0 flex items-center gap-3">
                                                    <div>
                                                        <div
                                                            className={`font-mono text-sm font-bold tabular-nums ${
                                                                idx === 0
                                                                    ? 'text-indigo-600 dark:text-indigo-400'
                                                                    : 'text-slate-700 dark:text-slate-300'
                                                            }`}
                                                        >
                                                            {(
                                                                (etf.similarity_score ??
                                                                    0) * 100
                                                            ).toFixed(1)}
                                                            %
                                                        </div>
                                                        <div className="text-[9px] uppercase font-bold tracking-widest mt-1 text-slate-400 dark:text-slate-500">
                                                            Match
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            openEtfInfo(
                                                                etf.symbol,
                                                            );
                                                        }}
                                                        aria-label={`Open ETF info for ${etf.symbol} in a new tab`}
                                                        className="px-3 py-1.5 rounded-lg text-xs font-semibold shadow-sm transition-colors
                                                                   bg-indigo-600 text-white hover:bg-indigo-700
                                                                   dark:bg-indigo-500 dark:hover:bg-indigo-600
                                                                   focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                                                    >
                                                        More Info…
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
}
